import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { request } from '../services/api';
import { useI18n } from '../../../i18n';
import { LoadingSpinner } from './LoadingSpinner';

interface Notificacion {
  id: string;
  tipo: string;
  titulo: string;
  mensaje: string;
  leido: boolean;
  creadoEn: string;
  // 0615+: payload JSONB con artefactoTipo + artefactoId para navegación.
  // Inyectado por notify-workflow.service al emitir la notificación.
  datos?: {
    artefactoTipo?: 'ORGANIGRAMA' | 'DOC' | 'FLUJOGRAMA' | 'PROCESO';
    artefactoId?: string;
    accionEjecutada?: string;
    estadoNuevo?: string;
    actorId?: string;
  } | null;
}

// 0656 (UX-2 cadena 0654): items de `/workflow/pendientes` que el panel
// muestra ARRIBA del listado de notifs históricas. Permite al aprobador
// ver pendientes vivos resolvidos via puesto+rol incluso si la transición
// no generó fila en M17.notificaciones (deuda histórica pre-fix 0653).
interface Pendiente {
  id: string;
  tipo: string;
  artefactoTipo: 'ORGANIGRAMA' | 'DOC' | 'FLUJOGRAMA' | 'PROCESO';
  artefactoId: string;
  titulo: string;
  estado: string;
  creadoEn: string;
  source?: 'sign' | 'workflow';
  // 0661.6: para navegacion canonica al panel caracterizacion modo Visor.
  codigoDocumento?: string | null;
  codigoProceso?: string | null;
}

interface NotificationPanelProps {
  onClose: () => void;
  onCountChange: (count: number) => void;
}

// 0661.6: rutas canonicas tras unificacion UI cross-artefacto (ADR-025).
// Mantener sincronizado con ActionCenterPage.buildDocUrl/buildFlujogramaUrl.
function urlFromNotificacion(n: Notificacion): string | null {
  const tipo = n.datos?.artefactoTipo;
  const id = n.datos?.artefactoId;
  if (!tipo) return null;
  if (tipo === 'ORGANIGRAMA') return `/structure?tab=organigrama`;
  // Para DOC/FLUJOGRAMA fallback a /process/explorador hasta que el payload
  // de notificaciones historicas incluya codigoDocumento/codigoProceso.
  if (tipo === 'DOC' && id) return `/process/explorador?versionId=${id}`;
  if (tipo === 'FLUJOGRAMA' && id) return `/process/explorador?flujogramaId=${id}`;
  return null;
}

// 0656/0661.6: pendientes vivos - prefiere panel caracterizacion modo Visor
// cuando el backend resolvio codigoDocumento + codigoProceso (DOC) o codigoProceso
// (FLUJOGRAMA). Si no, fallback al explorador legacy.
function urlFromPendiente(p: Pendiente): string {
  if (p.artefactoTipo === 'ORGANIGRAMA') return `/structure?tab=organigrama`;
  if (p.artefactoTipo === 'DOC') {
    if (p.codigoDocumento && p.codigoProceso) {
      return `/process?tab=caracterizacion&proceso=${encodeURIComponent(p.codigoProceso)}&doc=${encodeURIComponent(p.codigoDocumento)}&view=hidden`;
    }
    if (p.artefactoId) return `/process/explorador?versionId=${p.artefactoId}`;
  }
  if (p.artefactoTipo === 'FLUJOGRAMA') {
    if (p.codigoProceso) {
      return `/process?tab=caracterizacion&proceso=${encodeURIComponent(p.codigoProceso)}&view=flow`;
    }
    if (p.artefactoId) return `/process/explorador?flujogramaId=${p.artefactoId}`;
  }
  return '/sign/action-center';
}

function colorArtefacto(tipo: Pendiente['artefactoTipo']): string {
  if (tipo === 'ORGANIGRAMA') return '#1F3864';
  if (tipo === 'DOC') return '#2F5496';
  if (tipo === 'FLUJOGRAMA') return '#1A94BF';
  return '#33BCE1';
}

function tiempoRelativo(fecha: string): string {
  const diff = Date.now() - new Date(fecha).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'Ahora';
  if (min < 60) return `Hace ${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `Hace ${h}h`;
  return `Hace ${Math.floor(h / 24)}d`;
}

function iconPath(tipo: string): { path: string; color: string } {
  const TIPOS: Record<string, { path: string; color: string }> = {
    APROBACION_PENDIENTE: {
      path: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3',
      color: '#2F5496',
    },
    FIRMA_PENDIENTE: {
      path: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3',
      color: '#1A94BF',
    },
    COMPLETADO: {
      path: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3',
      color: '#38A169',
    },
    RECHAZADO: {
      path: 'M10 14l2-2m0 0 2-2m-2 2-2-2m2 2 2 2M3 12a9 9 0 1 1 18 0 9 9 0 0 1-18 0Z',
      color: '#E53E3E',
    },
    RECORDATORIO: {
      path: 'M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20M12 6v6l4 2',
      color: '#D69E2E',
    },
    ESCALACION: {
      path: 'M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20M12 6v6l4 2',
      color: '#DD6B20',
    },
    DOCUMENTO_ESTADO: {
      path: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6',
      color: '#2F5496',
    },
    PUBLICACION: {
      path: 'M3 11l19-9-9 19-2-8-8-2Z',
      color: '#33BCE1',
    },
    HALLAZGO_AUDITORIA: {
      path: 'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0ZM12 9v4M12 17h.01',
      color: '#E53E3E',
    },
    // 0628: notificaciones del workflow unificado (ADR-025).
    // Cubre transiciones de organigrama + docs + flujogramas en un solo tipo.
    WORKFLOW: {
      path: 'M12 2v6m0 0L9 5m3 3 3-3 M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0z',
      color: '#1F3864',
    },
  };
  return (
    TIPOS[tipo] ?? {
      path: 'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0',
      color: '#718096',
    }
  );
}

export function NotificationPanel({ onClose, onCountChange }: NotificationPanelProps) {
  const { t } = useI18n();
  const [items, setItems] = useState<Notificacion[]>([]);
  const [pendientes, setPendientes] = useState<Pendiente[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchAll = useCallback(async () => {
    try {
      const [notifsRes, pendientesRes] = await Promise.all([
        request<{ data: Notificacion[]; meta: { total: number } }>(
          '/notifications?pageSize=50',
        ).catch(() => ({ data: [], meta: { total: 0 } })),
        request<{ data: Pendiente[]; meta: { total: number } }>(
          '/workflow/pendientes?pageSize=20',
        ).catch(() => ({ data: [], meta: { total: 0 } })),
      ]);
      setItems(notifsRes.data ?? []);
      setPendientes(pendientesRes.data ?? []);
    } catch {
      /* silencioso */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const marcarLeida = async (id: string) => {
    const item = items.find((n) => n.id === id);
    if (!item || item.leido) return;

    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, leido: true } : n)));
    onCountChange(Math.max(0, items.filter((n) => !n.leido).length - 1));

    try {
      await request(`/notifications/${id}/read`, { method: 'PATCH' });
    } catch {
      /* revert si falla */
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, leido: false } : n)));
    }
  };

  const marcarTodas = async () => {
    const prevItems = [...items];
    const noLeidas = items.filter((n) => !n.leido).length;
    if (noLeidas === 0) return;

    setItems((prev) => prev.map((n) => ({ ...n, leido: true })));
    onCountChange(0);

    try {
      await request('/notifications/read-all', { method: 'PATCH' });
    } catch {
      setItems(prevItems);
    }
  };

  // 0615+: click en notificación → marcar leída + navegar al artefacto + cerrar panel.
  const handleClick = async (n: Notificacion) => {
    await marcarLeida(n.id);
    const url = urlFromNotificacion(n);
    if (url) {
      navigate(url);
      onClose();
    }
  };

  // 0656: click en pendiente vivo → navegar al artefacto + cerrar panel (no marca leído porque
  // los pendientes vivos no son notificaciones M17, son resoluciones runtime via /workflow/pendientes).
  const handlePendienteClick = (p: Pendiente) => {
    navigate(urlFromPendiente(p));
    onClose();
  };

  const noLeidas = items.filter((n) => !n.leido).length;
  const hayPendientes = pendientes.length > 0;

  return (
    <>
      {/* Overlay transparente */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 309,
          background: 'transparent',
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: 'fixed',
          right: 0,
          top: 48,
          height: 'calc(100vh - 48px)',
          width: 360,
          zIndex: 310,
          background: '#fff',
          boxShadow: '-4px 0 16px rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header del panel */}
        <div
          style={{
            padding: '14px 16px',
            borderBottom: '1px solid #E8E8E8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 15, color: '#1F3864' }}>
            {t.topbar.notifications.title}
          </span>
          {noLeidas > 0 && (
            <button
              onClick={marcarTodas}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 12,
                color: '#33BCE1',
                fontWeight: 600,
                padding: '4px 8px',
                borderRadius: 4,
              }}
            >
              ✓ {t.topbar.notifications.markAllRead}
            </button>
          )}
        </div>

        {/* Lista */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && <LoadingSpinner size="sm" muted />}

          {/* 0656: Sección "Pendientes vivos" arriba del listado de notifs históricas.
              Muestra items resueltos via /workflow/pendientes incluso si no hay fila M17. */}
          {!loading && hayPendientes && (
            <>
              <div
                style={{
                  padding: '10px 16px',
                  background: '#FFF8E1',
                  borderBottom: '1px solid #F0E0A0',
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#8B6914',
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                }}
              >
                {t.topbar.notifications.sectionPendientes} · {pendientes.length}
              </div>
              {pendientes.map((p) => {
                const tagColor = colorArtefacto(p.artefactoTipo);
                const tagLabel =
                  p.artefactoTipo === 'ORGANIGRAMA'
                    ? t.topbar.notifications.tagOrganigrama
                    : p.artefactoTipo === 'DOC'
                      ? t.topbar.notifications.tagDocumento
                      : p.artefactoTipo === 'FLUJOGRAMA'
                        ? t.topbar.notifications.tagFlujograma
                        : t.topbar.notifications.tagProceso;
                return (
                  <div
                    key={`pendiente-${p.id}`}
                    onClick={() => handlePendienteClick(p)}
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #F0F0F0',
                      display: 'flex',
                      gap: 10,
                      cursor: 'pointer',
                      background: '#FFFCF0',
                      transition: 'background 0.15s',
                    }}
                  >
                    {/* Icono workflow */}
                    <div style={{ flexShrink: 0, marginTop: 2 }}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={tagColor}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3" />
                      </svg>
                    </div>

                    {/* Contenido */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#1A202C',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {p.titulo}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: tagColor,
                          marginTop: 2,
                          fontWeight: 600,
                          letterSpacing: 0.3,
                          textTransform: 'uppercase',
                        }}
                      >
                        {tagLabel}
                      </div>
                    </div>

                    {/* Tiempo */}
                    <div
                      style={{
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'flex-start',
                        paddingTop: 2,
                      }}
                    >
                      <span style={{ fontSize: 11, color: '#999', whiteSpace: 'nowrap' }}>
                        {tiempoRelativo(p.creadoEn)}
                      </span>
                    </div>
                  </div>
                );
              })}
              <div
                style={{
                  padding: '10px 16px',
                  background: '#FAFBFC',
                  borderBottom: '1px solid #E8E8E8',
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#666',
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                }}
              >
                {t.topbar.notifications.sectionNotificaciones} · {items.length}
              </div>
            </>
          )}

          {!loading && items.length === 0 && !hayPendientes && (
            <div
              style={{
                padding: 48,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#CBD5E0"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              <span style={{ color: '#A0AEC0', fontSize: 13 }}>{t.topbar.notifications.empty}</span>
            </div>
          )}

          {!loading &&
            items.map((n) => {
              const icon = iconPath(n.tipo);
              const navigable = urlFromNotificacion(n) !== null;
              return (
                <div
                  key={n.id}
                  onClick={() => handleClick(n)}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #F0F0F0',
                    display: 'flex',
                    gap: 10,
                    cursor: navigable || !n.leido ? 'pointer' : 'default',
                    background: n.leido ? '#fff' : '#F0F7FF',
                    transition: 'background 0.15s',
                  }}
                >
                  {/* Icono */}
                  <div style={{ flexShrink: 0, marginTop: 2 }}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={icon.color}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d={icon.path} />
                    </svg>
                  </div>

                  {/* Contenido */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#1A202C',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {n.titulo}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: '#666',
                        marginTop: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {n.mensaje}
                    </div>
                  </div>

                  {/* Tiempo + dot */}
                  <div
                    style={{
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 6,
                    }}
                  >
                    <span style={{ fontSize: 11, color: '#999', whiteSpace: 'nowrap' }}>
                      {tiempoRelativo(n.creadoEn)}
                    </span>
                    {!n.leido && (
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: '#33BCE1',
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </>
  );
}
